import { subject } from "@casl/ability";
import axios from "axios";

import { getErrorDetails } from "api/helpers/getErrorDetails";
import { portalApi, TAGS } from "api/rtkApi";
import { DEFAULT_PAGINATION } from "constants/defaultPagination";
import { SERVICES } from "constants/environment";
import { SUBJECT_USER } from "models/role/subject";
import { pessimisticUpdate } from "utils/pessimisticUpdate";
import { preparePaginationParams } from "utils/preparePaginationParams";

import { prepareFilterParams } from "../utils/prepareFilterParams";

import type { AxiosRequestConfig } from "axios";

import type { RTKMeta } from "models/RTKMeta";
import type { UUID } from "utils";
import type { ObjectWithId } from "utils/ObjectWithId";
import type { PaginationParams } from "utils/preparePaginationParams";

import type { OrderableColumns } from "../constants";
import type { UserFilters } from "../models/Filters";
import type { BasicUser, User, UserNames } from "../models/User";
import type { UserForm } from "../models/UserForm";

interface SendProductInterestData {
  productName: string;
}

export async function sendProductInterest(
  data: SendProductInterestData,
  options: AxiosRequestConfig
) {
  try {
    await axios({
      ...options,
      method: "POST",
      url: "/users/settings/product-interest",
      data,
    });
  } catch (error) {
    throw getErrorDetails(error);
  }
}

type GetUsersResponse = {
  totalItems: number;
  users: User[];
};

type GetUsersRequest = {
  customerHierarchyId?: UUID;
  filters?: Partial<UserFilters>;
} & PaginationParams<OrderableColumns>;

type GetUsernamesRequest = {
  userIds?: UUID | UUID[];
};

type PatchActivateUserRequest = ObjectWithId;

type PatchDeactivateUserRequest = ObjectWithId;

export type GetUsernamesResponse = UserNames;

type PostRemoveScaMutation = ObjectWithId;

type UpdateUserRequest = {
  id: UUID;
  body: Pick<User, "email" | "name" | "permissions"> & {
    customer: UUID | null;
  };
};

type PostUserResponse = ObjectWithId;
export type PostUserRequest = Pick<
  UserForm,
  "customer" | "email" | "name" | "permissions"
> & { useNewPermissions: boolean };

export const userExtendedApi = portalApi.injectEndpoints({
  endpoints: (build) => ({
    postUser: build.mutation<PostUserResponse, PostUserRequest>({
      query: (body) => ({
        url: `users`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: TAGS.USERS }],
      extraOptions: {
        service: SERVICES.USERS,
      },
    }),
    getUsers: build.query<GetUsersResponse, GetUsersRequest>({
      query: ({ customerHierarchyId, filters, ...rest }) => ({
        url: "users",
        params: {
          customerHierarchyId,
          ...prepareFilterParams(filters),
          ...preparePaginationParams(rest),
        },
      }),
      providesTags: [{ type: TAGS.USERS }],
      transformResponse: (users: User[], meta: RTKMeta) => ({
        users,
        totalItems: Number(meta.response.headers.get("total-items")),
      }),
    }),
    getUser: build.query<User, UUID>({
      query: (userId) => `users/${userId}`,
      transformResponse: (user: User) => subject(SUBJECT_USER, user),
      providesTags: (_, __, id) => [{ type: TAGS.USER, id }],
    }),
    getUsernames: build.query<GetUsernamesResponse, GetUsernamesRequest>({
      query: ({ userIds }) => ({
        url: "users",
        method: "GET",
        params: {
          id: userIds,
          ...DEFAULT_PAGINATION,
        },
      }),
      transformResponse: (data: BasicUser[]) =>
        data.reduce<GetUsernamesResponse>(
          (names, user) => ({
            ...names,
            [user.id]: user.name,
          }),
          {}
        ),
      providesTags: [TAGS.USER_NAMES],
    }),
    resetUserPassword: build.mutation<UUID, UUID>({
      query: (id) => ({
        url: `users/${id}/password/reset`,
        method: "POST",
      }),
      extraOptions: {
        service: SERVICES.USERS,
      },
    }),
    putUser: build.mutation<User, UpdateUserRequest>({
      query: ({ id, body }) => ({
        url: `users/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: TAGS.USER, id },
        { type: TAGS.USERS },
      ],
      extraOptions: {
        service: SERVICES.USERS,
      },
    }),
    promoteUserToOwner: build.mutation<UUID, UUID>({
      query: (id) => ({
        url: `users/${id}/promote`,
        method: "PATCH",
      }),
      extraOptions: {
        service: SERVICES.USERS,
      },
      onQueryStarted(id, { dispatch, queryFulfilled }) {
        return pessimisticUpdate(queryFulfilled)(() => {
          dispatch(userExtendedApi.util.invalidateTags([{ type: TAGS.USERS }]));
          dispatch(
            userExtendedApi.util.updateQueryData(
              "getUser",
              id,
              (draft: User) => {
                draft.owner = true;
              },
              true
            )
          );
        });
      },
    }),
    activateUser: build.mutation<void, PatchActivateUserRequest>({
      query: ({ id }) => ({
        url: `users/${id}/activate`,
        method: "PATCH",
      }),
      extraOptions: {
        service: SERVICES.USERS,
      },
      onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        return pessimisticUpdate(queryFulfilled)(() => {
          dispatch(
            userExtendedApi.util.updateQueryData(
              "getUser",
              id,
              (cachedUser: User) => {
                cachedUser.active = true;
              }
            )
          );
        });
      },
      invalidatesTags: [{ type: TAGS.USERS }],
    }),
    deactivateUser: build.mutation<void, PatchDeactivateUserRequest>({
      query: ({ id }) => ({
        url: `users/${id}/deactivate`,
        method: "PATCH",
      }),
      extraOptions: {
        service: SERVICES.USERS,
      },
      onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        return pessimisticUpdate(queryFulfilled)(() => {
          dispatch(
            userExtendedApi.util.updateQueryData(
              "getUser",
              id,
              (cachedUser: User) => {
                cachedUser.active = false;
              }
            )
          );
        });
      },
      invalidatesTags: [{ type: TAGS.USERS }],
    }),
    deleteUserScaAuthentication: build.mutation<void, PostRemoveScaMutation>({
      query: ({ id }) => ({
        url: `users/${id}/sca`,
        method: "DELETE",
      }),
      extraOptions: {
        service: SERVICES.USERS,
      },
      invalidatesTags: [{ type: TAGS.USERS }],
      onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        return pessimisticUpdate(queryFulfilled)(() => {
          dispatch(
            userExtendedApi.util.invalidateTags([
              {
                type: TAGS.USERS,
              },
            ])
          );

          dispatch(
            userExtendedApi.util.updateQueryData(
              "getUser",
              id,
              (draft: User) => {
                Object.assign(draft, { scaAuthentication: { active: false } });
              }
            )
          );
        });
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUserQuery,
  useGetUsersQuery,
  useGetUsernamesQuery,
  useResetUserPasswordMutation,
  usePutUserMutation,
  usePromoteUserToOwnerMutation,
  useDeleteUserScaAuthenticationMutation,
  useActivateUserMutation,
  useDeactivateUserMutation,
  usePostUserMutation,
} = userExtendedApi;

export const { getUser } = userExtendedApi.endpoints;
